// Where plan items sit when the project's dates change - "I need X for 5 days from the start".
//
// A role or cost line pinned to one end follows that project date and keeps its length.
// When the project gets too short it shrinks to fit and remembers the length it asked for
// (`requestedDays`), so it grows back with the project. Pinned to both ends, it spans the
// whole project. An item that isn't pinned stays where it is - unless the whole project
// moves, and then it moves with it.
//
// What has started stays where it happened: a pinned item that has started (or has time
// logged on it) only moves its pinned end with the project - its other end stays, so the
// weeks already worked never fall outside the role.
//
// A role that came from GT's CRM (`createdFromGtCrm`) is reference only - the hours the CRM
// asked for. Nothing here moves it: it follows the project's dates where it always did
// (pinned to both ends) and is left alone otherwise.
//
// Lengths are kept in the unit the item is thought of in: roles in working days (the way
// their hours are spread) unless they include weekends, monthly cost lines in months, other
// cost lines and equipment lines in calendar days.

import {
    addBusinessDays,
    addDays,
    addMonths,
    differenceInBusinessDays,
    differenceInCalendarDays,
    differenceInCalendarMonths,
    isLastDayOfMonth,
    lastDayOfMonth,
    startOfMonth,
} from "date-fns"
import { endOfDay, isAfter, isBefore, isSameDay, isSameMonth, isWeekend, startOfDay, subDays } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { getTimeUtc } from "./dates"
import { getLinePins, isEquipmentLine, toPinnedTo } from "./equipment-demand"
import { hasPlanItemStarted } from "./started-work"

export const countsCalendarDays = (pi = {}) => pi.type === "cost" || Boolean(pi.includeWeekends)

/** A GT CRM role: reference data, never moved or resized by us. */
export const isCrmReference = (pi = {}) => pi.createdFromGtCrm === true

const nextWorkingDay = (date) => {
    let d = date
    while (isWeekend(d)) d = addDays(d, 1)
    return d
}

const prevWorkingDay = (date) => {
    let d = date
    while (isWeekend(d)) d = subDays(d, 1)
    return d
}

/** How many whole months [start, end] is (1 Feb - 30 Apr is 3, so is 10 Feb - 9 May), or null. */
const getMonthsBetween = (start, end) => {
    const dayAfter = addDays(end, 1)
    const months = differenceInCalendarMonths(dayAfter, start)

    return months >= 1 && isSameDay(addMonths(start, months), dayAfter) ? months : null
}

/**
 * How many days a plan item spans - working days for roles, calendar days for cost lines.
 * Never less than 1, even for a bad range.
 */
export const getSpanDays = (pi = {}, calendar = countsCalendarDays(pi)) => {
    const start = startOfDay(toZonedTime(pi.startDate))
    const end = startOfDay(toZonedTime(pi.endDate))

    const days = calendar
        ? differenceInCalendarDays(end, start) + 1
        : differenceInBusinessDays(prevWorkingDay(end), nextWorkingDay(start)) + 1

    return Number.isFinite(days) ? Math.max(1, days) : 1
}

/**
 * How many whole months a monthly cost line runs. Null when it isn't whole months, or
 * isn't a monthly line - equipment lines are day-precise whatever they're priced in.
 */
export const getWholeMonths = (pi = {}) => {
    if (pi.type !== "cost" || pi.rateTime !== "Monthly" || isEquipmentLine(pi)) return null

    return getMonthsBetween(startOfDay(toZonedTime(pi.startDate)), startOfDay(toZonedTime(pi.endDate)))
}

/**
 * Where a pinned plan item sits for the project's dates.
 *
 * @param pi {object} The plan item
 * @param mission {object} The project with its new dates
 * @param [today] {Date}
 * @return {{ startDate: Number, endDate: Number, requestedDays: Number|null }|null} null when
 *   no end is pinned
 */
export const getPinnedDates = (pi = {}, mission = {}, today = new Date()) => {
    // A line tied to a task follows the task (utils/mission.js#reconcileCostItems), never the project
    if (pi.linkedToTask) return null

    const pins = getLinePins(pi)
    const planStart = mission.planStartDate
    const planEnd = mission.planEndDate

    if ((!pins.start && !pins.end) || !planStart || !planEnd) return null

    if (pins.start && pins.end) return { startDate: planStart, endDate: planEnd, requestedDays: null }

    // Started, has time logged on it, or came from the CRM: only the pinned end follows the
    // project, the other end stays where it is (brought inside the project when it no
    // longer fits)
    if (
        pi.startDate &&
        pi.endDate &&
        (isCrmReference(pi) || hasPlanItemStarted(pi, today) || pi.timesheets?.length > 0)
    ) {
        const startDate = pins.start ? planStart : Math.max(pi.startDate, planStart)
        const endDate = pins.end ? planEnd : Math.min(pi.endDate, planEnd)

        if (startDate <= endDate) return { startDate, endDate, requestedDays: pi.requestedDays || null }
    }

    const calendar = countsCalendarDays(pi)
    const current = getSpanDays(pi, calendar)

    // Squeezed by a shorter project: it wants its full length back. Editing the dates by
    // hand clears requestedDays (App.planItemPrepareDateEdit / planItemUpdateBulk)
    const isSqueezed = pi.requestedDays > current
    const days = isSqueezed ? pi.requestedDays : current
    const months = isSqueezed ? null : getWholeMonths(pi)

    let start, end

    if (pins.start) {
        start = startOfDay(toZonedTime(planStart))
        end = months
            ? subDays(addMonths(start, months), 1)
            : calendar
            ? addDays(start, days - 1)
            : addBusinessDays(nextWorkingDay(start), days - 1)
    } else {
        end = startOfDay(toZonedTime(planEnd))
        start = months
            ? addMonths(addDays(end, 1), -months)
            : calendar
            ? subDays(end, days - 1)
            : addBusinessDays(prevWorkingDay(end), -(days - 1))
    }

    const startDate = pins.start ? planStart : Math.max(getTimeUtc(startOfDay(start)), planStart)
    const endDate = pins.end ? planEnd : Math.min(getTimeUtc(endOfDay(end)), planEnd)

    const wanted = months ? differenceInCalendarDays(end, start) + 1 : days
    const fitted = getSpanDays({ ...pi, startDate, endDate }, calendar)

    return { startDate, endDate, requestedDays: fitted < wanted ? wanted : null }
}

/** Whether getPinnedDates() moved anything. */
export const isPinnedChange = (pi = {}, next) =>
    Boolean(next) &&
    (next.startDate !== pi.startDate ||
        next.endDate !== pi.endDate ||
        (pi.requestedDays || null) !== next.requestedDays)

/**
 * Whether the project moved: both of its dates went the same way. One date changing, or
 * the two moving apart, is the project getting longer or shorter - nothing moves with that.
 *
 * @param mission {object} The project with its new dates
 * @param beforeStart {number} planStartDate before the change
 * @param beforeEnd {number} planEndDate before the change
 * @return {{ fromStart: Number, toStart: Number }|null}
 */
export const getProjectMove = (mission = {}, beforeStart, beforeEnd) => {
    if (!beforeStart || !beforeEnd || !mission.planStartDate || !mission.planEndDate) return null

    const startDays = differenceInCalendarDays(toZonedTime(mission.planStartDate), toZonedTime(beforeStart))
    const endDays = differenceInCalendarDays(toZonedTime(mission.planEndDate), toZonedTime(beforeEnd))

    if (!startDays || Math.sign(startDays) !== Math.sign(endDays)) return null

    return { fromStart: beforeStart, toStart: mission.planStartDate }
}

/**
 * A role or equipment line that isn't pinned to anything, after the project (or what's left
 * of it) moved: it moves as far as the start did and keeps its length. What has started
 * stays where it is - only work that hasn't started moves. Pinned items and roles tied to
 * a task follow their own rules.
 *
 * @param pi {object} The plan item
 * @param move {{ fromStart: Number, toStart: Number }} From getProjectMove(), or a shift
 * @return {{ startDate: Number, endDate: Number }|null} null when it doesn't move
 */
export const getMovedDates = (pi = {}, move, today = new Date()) => {
    const isRole = pi.type === "person"

    if (!move || pi.pinnedTo || pi.linkedToTask || isCrmReference(pi) || (!isRole && !isEquipmentLine(pi))) return null

    const start = startOfDay(toZonedTime(pi.startDate))
    const end = startOfDay(toZonedTime(pi.endDate))

    if (hasPlanItemStarted(pi, today)) return null

    const from = startOfDay(toZonedTime(move.fromStart))
    const to = startOfDay(toZonedTime(move.toStart))

    let newStart, newEnd

    if (!isRole || pi.includeWeekends) {
        const days = differenceInCalendarDays(to, from)

        newStart = addDays(start, days)
        newEnd = addDays(end, days)
    } else {
        const days = differenceInBusinessDays(nextWorkingDay(to), nextWorkingDay(from))

        if (!days) return null

        newStart = addBusinessDays(nextWorkingDay(start), days)
        newEnd = addBusinessDays(newStart, getSpanDays(pi, false) - 1)
    }

    return { startDate: getTimeUtc(startOfDay(newStart)), endDate: getTimeUtc(endOfDay(newEnd)) }
}

/**
 * Where a cost line that isn't pinned sits after the project's dates change. Null when it
 * stays as it is - which it does whenever it still fits and the project didn't move.
 *
 * - The whole project moved: the line moves with it and keeps its length, unless it has
 *   started. Monthly and one-off lines are placed by month, so they move by whole months;
 *   other lines by days.
 * - It now starts before the project: it moves to the project's start and keeps its length.
 *   A one-off cost lands in that first month.
 * - It now runs past the project's end: a recurring line stops with the project, a one-off
 *   cost lands in the last month.
 * - It ended with the project, and the project's end moved within that month: it still
 *   ends with the project.
 *
 * Equipment lines are day-precise and have their own rules (getPinnedDates, getMovedDates).
 * A line tied to a task follows the task (utils/mission.js#reconcileCostItems).
 *
 * @param pi {object} The cost line
 * @param mission {object} The project with its new dates
 * @param before {{ startDate: Number, endDate: Number }} The project's dates before the change
 * @param [options] {{ move: object, today: Date }} move: a shift of what hasn't started
 *   ({fromStart, toStart}) instead of the project's own move
 * @return {{ startDate: Number, endDate: Number }|null}
 */
export const getCostLineDates = (pi = {}, mission = {}, before = {}, options = {}) => {
    if (pi.type !== "cost" || isEquipmentLine(pi) || pi.linkedToTask) return null

    const planStart = pi.isForecast ? mission.forecastStartDate : mission.planStartDate
    const planEnd = pi.isForecast ? mission.forecastEndDate : mission.planEndDate

    if (!planStart || !planEnd || !pi.startDate || !pi.endDate) return null

    const first = startOfDay(toZonedTime(planStart))
    const last = startOfDay(toZonedTime(planEnd))
    const lineStart = startOfDay(toZonedTime(pi.startDate))
    const lineEnd = startOfDay(toZonedTime(pi.endDate))

    if (isAfter(first, last) || isAfter(lineStart, lineEnd)) return null

    const isOneOff = pi.rateTime === "One time"
    const months = getMonthsBetween(lineStart, lineEnd)
    const days = differenceInCalendarDays(lineEnd, lineStart) + 1
    const later = (a, b) => (isAfter(a, b) ? a : b)

    let start = lineStart
    let end = lineEnd

    // What has started stays where it is
    const move =
        pi.isForecast || pi.pinnedTo || isCrmReference(pi) || hasPlanItemStarted(pi, options.today)
            ? null
            : options.move || getProjectMove(mission, before.startDate, before.endDate)

    if (move) {
        const from = toZonedTime(move.fromStart)
        const to = toZonedTime(move.toStart)

        if (isOneOff || pi.rateTime === "Monthly") {
            const by = differenceInCalendarMonths(to, from)

            if (by) {
                start = addMonths(lineStart, by)
                end = months
                    ? subDays(addMonths(start, months), 1)
                    : isLastDayOfMonth(lineEnd)
                    ? lastDayOfMonth(addMonths(lineEnd, by))
                    : addMonths(lineEnd, by)
            }
        } else {
            const by = differenceInCalendarDays(to, from)

            start = addDays(lineStart, by)
            end = addDays(lineEnd, by)
        }
    }

    if (isBefore(start, first)) {
        start = first
        end = isOneOff
            ? lastDayOfMonth(first)
            : months
            ? subDays(addMonths(first, months), 1)
            : addDays(first, days - 1)
    } else if (isAfter(start, last)) {
        end = last
        start = later(
            isOneOff ? startOfMonth(last) : months ? addMonths(addDays(last, 1), -months) : subDays(last, days - 1),
            first,
        )
    }

    if (isAfter(end, last)) {
        end = last

        if (isOneOff) start = later(startOfMonth(last), first)
    } else if (
        !pi.isForecast &&
        before.endDate &&
        before.startDate === planStart &&
        isSameDay(lineEnd, toZonedTime(before.endDate)) &&
        isSameMonth(lineEnd, last) &&
        isSameDay(end, lineEnd)
    ) {
        end = last
    }

    if (isSameDay(start, lineStart) && isSameDay(end, lineEnd)) return null

    return {
        startDate: isSameDay(start, first) ? planStart : getTimeUtc(startOfDay(start)),
        endDate: isSameDay(end, last) ? planEnd : getTimeUtc(endOfDay(end)),
    }
}

/**
 * Moving a pinned end by hand unpins it - otherwise the next project date change would
 * snap it back. An end set to the project's own date stays pinned.
 *
 * @return {String|null|undefined} the new pinnedTo, undefined when nothing changes
 */
export const getHandEditPinnedTo = (pi = {}, change = {}, mission) => {
    if (!mission || Object.prototype.hasOwnProperty.call(change, "pinnedTo")) return undefined

    const pins = getLinePins(pi)

    if (!pins.start && !pins.end) return undefined

    const next = { ...pins }

    if (pins.start && change.startDate && !isSameDay(toZonedTime(change.startDate), toZonedTime(mission.planStartDate))) {
        next.start = false
    }
    if (pins.end && change.endDate && !isSameDay(toZonedTime(change.endDate), toZonedTime(mission.planEndDate))) {
        next.end = false
    }

    return next.start === pins.start && next.end === pins.end ? undefined : toPinnedTo(next)
}

/**
 * Whether a change moves the dates of a role or an equipment line - what hangs off it then
 * has to follow: the role's schedule, the line's machine bookings (the API's
 * planItemSchedule.shift.js; the plan item itself remembers the dates they reflect).
 *
 * A CRM role is never scheduled, so nothing follows its dates.
 */
export const hasScheduleChange = (pi = {}, change = {}) => {
    if ((pi.type !== "person" && !isEquipmentLine(pi)) || !pi._id || !change) return false
    if (isCrmReference(pi)) return false

    const moved = (key) => change[key] !== undefined && change[key] !== null && change[key] !== pi[key]

    return moved("startDate") || moved("endDate")
}
